import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Custom, premium video player matching the YouShop brand palette
 * (brand orange #F26522 + navy #1B2D4B). Fully self-contained controls:
 * play/pause, seek, volume, speed, fullscreen, big-play overlay, glow.
 */
export default function VideoPlayer({ src, poster, className = '' }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const hideTimer = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [showSpeed, setShowSpeed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);

  const speeds = [0.5, 1, 1.25, 1.5, 2];

  const fmt = (s) => {
    if (!s || Number.isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setStarted(true);
    } else {
      v.pause();
    }
  }, []);

  // Sync events
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTime = () => {
      setCurrent(v.currentTime);
      if (v.buffered.length) setBuffered(v.buffered.end(v.buffered.length - 1));
    };
    const onMeta = () => setDuration(v.duration);
    const onEnd = () => setIsPlaying(false);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('ended', onEnd);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('ended', onEnd);
    };
  }, []);

  // Fullscreen state
  useEffect(() => {
    const onFs = () => setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Auto-hide controls while playing
  const nudgeControls = useCallback(() => {
    setControlsVisible(true);
    clearTimeout(hideTimer.current);
    if (isPlaying) {
      hideTimer.current = setTimeout(() => {
        setControlsVisible(false);
        setShowSpeed(false);
      }, 2600);
    }
  }, [isPlaying]);

  useEffect(() => {
    nudgeControls();
    return () => clearTimeout(hideTimer.current);
  }, [isPlaying, nudgeControls]);

  const seek = (e) => {
    const bar = progressRef.current;
    const v = videoRef.current;
    if (!bar || !v || !duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    v.currentTime = ratio * duration;
    setCurrent(v.currentTime);
  };

  const changeVolume = (val) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = val;
    v.muted = val === 0;
    setVolume(val);
    setMuted(val === 0);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    if (!next && v.volume === 0) changeVolume(0.6);
  };

  const pickSpeed = (s) => {
    const v = videoRef.current;
    if (v) v.playbackRate = s;
    setSpeed(s);
    setShowSpeed(false);
  };

  const toggleFullscreen = () => {
    const c = containerRef.current;
    if (!document.fullscreenElement) c?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const progressPct = duration ? (current / duration) * 100 : 0;
  const bufferedPct = duration ? (buffered / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      dir="ltr"
      onMouseMove={nudgeControls}
      onMouseLeave={() => isPlaying && setControlsVisible(false)}
      className={`group relative w-full overflow-hidden rounded-3xl bg-navy-900 shadow-2xl shadow-navy-900/40 ring-1 ring-white/10 ${className}`}
    >
      {/* Ambient brand glow */}
      <div className="pointer-events-none absolute -inset-px rounded-3xl bg-gradient-to-tr from-brand-500/10 via-transparent to-brand-500/5" />

      <video
        ref={videoRef}
        src={src}
        poster={poster}
        playsInline
        preload="metadata"
        onClick={togglePlay}
        className="relative z-10 aspect-video w-full cursor-pointer bg-black object-contain"
      />

      {/* Big center play / pause overlay */}
      <button
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        className={`absolute inset-0 z-20 flex items-center justify-center transition-opacity duration-300 ${
          isPlaying ? 'opacity-0 group-hover:opacity-0' : 'opacity-100'
        }`}
      >
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-500/90 shadow-xl shadow-brand-500/40 ring-4 ring-white/20 backdrop-blur-sm transition-transform duration-300 hover:scale-110 sm:h-24 sm:w-24">
          {isPlaying ? (
            <svg className="h-9 w-9 text-white" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg className="ml-1 h-10 w-10 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.79-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14z" />
            </svg>
          )}
        </span>
      </button>

      {/* Gradient scrim + controls */}
      <div
        className={`absolute inset-x-0 bottom-0 z-30 bg-gradient-to-t from-navy-900/95 via-navy-900/40 to-transparent px-4 pb-3 pt-16 transition-all duration-300 sm:px-5 ${
          controlsVisible || !started ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        }`}
      >
        {/* Progress bar */}
        <div
          ref={progressRef}
          onClick={seek}
          className="group/bar relative mb-3 h-1.5 w-full cursor-pointer rounded-full bg-white/20 transition-all hover:h-2.5"
        >
          <div className="absolute inset-y-0 left-0 rounded-full bg-white/20" style={{ width: `${bufferedPct}%` }} />
          <div className="absolute inset-y-0 left-0 rounded-full bg-brand-500" style={{ width: `${progressPct}%` }} />
          <div
            className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-0 shadow-md ring-2 ring-brand-500 transition-opacity group-hover/bar:opacity-100"
            style={{ left: `${progressPct}%` }}
          />
        </div>

        {/* Buttons row */}
        <div className="flex items-center gap-3 text-white">
          <button onClick={togglePlay} aria-label="Play/Pause" className="transition-transform hover:scale-110">
            {isPlaying ? (
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.79-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14z" />
              </svg>
            )}
          </button>

          {/* Volume */}
          <div className="group/vol flex items-center">
            <button onClick={toggleMute} aria-label="Mute" className="transition-transform hover:scale-110">
              {muted || volume === 0 ? (
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 9v6h4l5 5V4L7 9H3z" />
                  <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M16 9l5 5m0-5l-5 5" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 9v6h4l5 5V4L7 9H3z" />
                  <path d="M16.5 12a3.5 3.5 0 0 0-2-3.16v6.32A3.5 3.5 0 0 0 16.5 12z" />
                  <path d="M14 3.23v2.06a7 7 0 0 1 0 13.42v2.06a9 9 0 0 0 0-17.54z" />
                </svg>
              )}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={muted ? 0 : volume}
              onChange={(e) => changeVolume(parseFloat(e.target.value))}
              aria-label="Volume"
              className="ml-1 w-0 cursor-pointer accent-brand-500 opacity-0 transition-all duration-300 group-hover/vol:w-20 group-hover/vol:opacity-100"
            />
          </div>

          {/* Time */}
          <span className="ml-1 select-none text-xs font-medium tabular-nums text-white/90 sm:text-sm">
            {fmt(current)} <span className="text-white/40">/</span> {fmt(duration)}
          </span>

          <div className="flex-1" />

          {/* Speed */}
          <div className="relative">
            <button
              onClick={() => setShowSpeed((s) => !s)}
              className="rounded-md px-2 py-1 text-xs font-semibold text-white/90 ring-1 ring-white/20 transition-colors hover:bg-white/10 sm:text-sm"
            >
              {speed}×
            </button>
            {showSpeed && (
              <div className="absolute bottom-10 right-0 flex flex-col overflow-hidden rounded-xl bg-navy-800 p-1 shadow-xl ring-1 ring-white/10">
                {speeds.map((s) => (
                  <button
                    key={s}
                    onClick={() => pickSpeed(s)}
                    className={`rounded-lg px-4 py-1.5 text-left text-sm transition-colors hover:bg-white/10 ${
                      s === speed ? 'text-brand-500 font-semibold' : 'text-white/80'
                    }`}
                  >
                    {s}×
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Fullscreen */}
          <button onClick={toggleFullscreen} aria-label="Fullscreen" className="transition-transform hover:scale-110">
            {isFullscreen ? (
              <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 9H5V5m0 4l5-5M15 9h4V5m0 4l-5-5M9 15H5v4m0-4l5 5m5-5h4v4m0-4l-5 5" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 9V5a1 1 0 0 1 1-1h4M20 9V5a1 1 0 0 0-1-1h-4M4 15v4a1 1 0 0 0 1 1h4M20 15v4a1 1 0 0 1-1 1h-4" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
